import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, of, map, concatMap, forkJoin, exhaustMap } from "rxjs";
import { EventComplete } from "../../models/events/event-complete.model";
import { AdminService } from "../../admin.service";
import { adminActions, adminApiActions } from "../actions/admin.actions";
import { selectAdminAllEvents, selectAdminArchiveLoaded, selectAdminEvent, selectAdminEventHosts, selectAdminPrivilegedUsers, selectAdminTicketTypes, selectAdminUser, selectAdminUsers, selectAllAdminOrganisers, selectCurrentLoaded } from "../selectors/admin.selectors";
import { concatLatestFrom } from "@ngrx/operators";
import { OrganiserService } from "../../organisers.service";

@Injectable()
export class AdminEffects {
    private action$ = inject(Actions);
    private service = inject(AdminService);
    private organiserService = inject(OrganiserService);
    private store = inject(Store);

    loadAdminUpcomingEvents$ = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.getAllEvents),
            withLatestFrom(this.store.select(selectCurrentLoaded)),
            switchMap(([action,data]) => {
                if (data){
                    return of();
                }
                else {
                    return this.service.getEvents().pipe(
                        map(data => adminApiActions.fetchAllEvents({ events:data as EventComplete[]}))
                    )
                }
            })
        )
    })

    loadAdminArchiveEvents$ = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.getArchivedEvents),
            withLatestFrom(this.store.select(selectAdminArchiveLoaded)),
            switchMap(([action,data]) => {
                if (data) {
                    return of();
                }
                else {
                    return this.service.getArchivedEvents().pipe(
                        map(data => adminApiActions.fetchAllEvents({ events:data as EventComplete[]}))
                    )
                }
            })
        )
    })
    
    loadAdminSingleEvent$ = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.getEvent),
            withLatestFrom(this.store.select(selectAdminAllEvents)),
            switchMap(([action, data]) => {
                if (data.find(x => x.eventLink === action.eventRef && x.complete === true)){
                    return of();
                }
                else {
                    return this.service.getEvent(action.eventRef).pipe(
                        map(data => adminApiActions.fetchEvent({ event: data as EventComplete}))
                    )
                }
            })
        )
    })

    loadEventHosts$ = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.getEventHosts),
            withLatestFrom(this.store.select(selectAdminEventHosts)),
            exhaustMap(([action, data]) => {
                return this.service.getEventHosts().pipe(
                    map(data => adminApiActions.fetchEventHosts({ eventHosts: data}))
                )
            })
        )
    })

    loadPrivilegedUsers = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.getUsers),
            concatMap(() => 
                forkJoin([
                    this.service.getPrivilegedUsers(),
                    this.service.getUserPermissions()
                ]).pipe(
                    map(data => adminApiActions.fetchUsers({ user: data })),
                 ))
        )
    })

    // loadSinglePrivilegedUser = createEffect(() => {
    //     return this.action$.pipe(
    //         ofType(adminActions.getSinglePrivilegedUser),
    //         withLatestFrom(this.store.select(selectAdminPrivilegedUsers)),
    //         switchMap(([action, data]) => {
    //             if (data) {
    //                 if (data.find(x => x.id === action.userId && x.dob !== undefined)) {
    //                     return of();
    //                 }
    //             }
    //             return this.service.getUser(action.userId).pipe(
    //                 map(data => adminApiActions.fetchSingleUser({ user: data }))
    //             )
    //         })
    //     )
    // })

    // loadSingleUser = createEffect(() => {
    //     return this.action$.pipe(
    //         ofType(adminActions.getSingleUser),
    //         withLatestFrom(this.store.select(selectAdminUsers)),
    //         switchMap(([action,data]) => {
    //             if (data){
    //                 if (data.find(x => x.id === action.userId)){
    //                     return of();
    //                 }
    //             }
    //             return this.service.getUser(action.userId).pipe(
    //                 map(data => adminApiActions.fetchSingleUser({ user: data }))
    //             )
    //         })
    //     )
    // })

    createEvent = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.createNewEvent),
            exhaustMap(action => this.service.createEvent(action.lrpEvent).pipe(
                map(lrpEvent => adminApiActions.eventCreated({ lrpEvent }))
            ))
        )
    })

    fetchTicketTypes$ = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.getTicketTypes),
            concatLatestFrom(() => this.store.select(selectAdminTicketTypes)),
            switchMap(([action,data]) => {
                if (data.length > 0){
                    return of();
                }
                return this.service.getEventTicketTypes().pipe(
                    map(ticketTypes => adminApiActions.fetchTicketTypes({ ticketTypes }))
                )
            })
        )
    })

    getOrganisers$ = createEffect(() => {
        return this.action$.pipe(
            ofType(adminActions.getOrganisers),
            concatLatestFrom(() => this.store.select(selectAllAdminOrganisers)),
            switchMap(([action,data]) => {
                if (data.length > 0){
                    return of();
                }
                return this.service.getOrganisers().pipe(
                    map(organisers => adminApiActions.fetchOrganisers({ organisers }))
                )
            })
        )
    })
}